export interface ContentSeed {
  id: string;
  timestamp: string;
  source_url: string | null;
  source_name: string | null;
  domain: string | null;
  keywords: string | null;
  extracted_snippet: string | null;
  full_summary: string | null;
  relevance_score: number | null;
  DPIF_tags: string | null;
  sentiment: string | null;
  status: string;
  notes: string | null;
}

export interface ContentBrief {
  id: string;
  created_at: string;
  updated_at: string;
  source_seed_ids: string;
  channel: string;
  headline: string;
  angle: string;
  key_points: string;
  dpif_connection: string | null;
  timeliness_score: number;
  strategic_score: number;
  composite_priority: number;
  status: string;
  writing_agent_notes: string | null;
  human_notes: string | null;
}

export interface ContentDraft {
  id: string;
  created_at: string;
  updated_at: string;
  brief_id: string;
  channel: string;
  headline: string;
  body: string;
  meta_description: string | null;
  suggested_tags: string | null;
  word_count: number;
  status: string;
  revision_notes: string | null;
}

export interface PipelineStats {
  seeds_new: number;
  seeds_processed: number;
  seeds_total: number;
  briefs_ready: number;
  briefs_in_writing: number;
  briefs_in_editing: number;
  briefs_staged: number;
  briefs_published: number;
  briefs_total: number;
  drafts_draft: number;
  drafts_in_editing: number;
  drafts_staged: number;
  drafts_published: number;
  drafts_total: number;
}

export interface WeeklyObjective {
  id: string;
  week_start: string;
  week_end: string;
  targets: string;
  weights: string;
  created_at: string;
  updated_at: string;
  notes: string | null;
}

export interface WeeklyScore {
  id: string;
  objective_id: string;
  week_start: string;
  actuals: string;
  scores: string;
  category_scores: string;
  overall_score: number;
  grade: string;
  computed_at: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface OpsLogEntry {
  id: string;
  timestamp: string;
  agent: string;
  action: string;
  details: string | null;
  entity_type: string | null;
  entity_id: string | null;
  created_at: string;
}

export interface AgentStatus {
  key: string;
  name: string;
  lastActivity: string | null;
  hoursAgo: number | null;
  output: string | null;
  schedule: string;
  scheduleDesc: string;
  url: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export type MetricDirection = 'higher' | 'lower' | 'range';

export interface MetricDef {
  key: string;
  label: string;
  shortLabel: string;
  category: 'volume' | 'quality' | 'efficiency';
  direction: MetricDirection;
  rangeMin?: number;
  rangeMax?: number;
  format: 'int' | 'float' | 'pct';
}

export const METRIC_DEFS: MetricDef[] = [
  { key: 'seeds_collected',        label: 'Seeds collected',    shortLabel: 'Seeds\ncollected',    category: 'volume',     direction: 'higher', format: 'int' },
  { key: 'briefs_generated',       label: 'Briefs generated',   shortLabel: 'Briefs\ngenerated',   category: 'volume',     direction: 'higher', format: 'int' },
  { key: 'drafts_written',         label: 'Drafts written',     shortLabel: 'Drafts\nwritten',     category: 'volume',     direction: 'higher', format: 'int' },
  { key: 'pieces_published',       label: 'Published',          shortLabel: 'Published',            category: 'volume',     direction: 'higher', format: 'int' },
  { key: 'avg_composite_priority', label: 'Avg priority score', shortLabel: 'Avg priority\nscore', category: 'quality',    direction: 'higher', format: 'float' },
  { key: 'editing_pass_rate',      label: 'Editing pass rate',  shortLabel: 'Edit pass\nrate',      category: 'quality',    direction: 'higher', format: 'pct' },
  { key: 'rejection_rate',         label: 'Rejection rate',     shortLabel: 'Rejection\nrate',      category: 'quality',    direction: 'lower',  format: 'pct' },
  { key: 'seed_promotion',         label: 'Seed promotion',     shortLabel: 'Seed\npromotion',      category: 'efficiency', direction: 'higher', format: 'pct' },
  { key: 'review_turnaround',      label: 'Review turnaround',  shortLabel: 'Review\nturnaround',   category: 'efficiency', direction: 'lower',  format: 'int' },
];

export const DEFAULT_TARGETS: Record<string, number> = {
  seeds_collected: 250,
  briefs_generated: 15,
  drafts_written: 8,
  pieces_published: 5,
  avg_composite_priority: 0.75,
  editing_pass_rate: 80,
  rejection_rate: 20,
  seed_promotion: 10,
  review_turnaround: 24,
};

export const DEFAULT_WEIGHTS: Record<string, number> = {
  volume: 0.40,
  quality: 0.35,
  efficiency: 0.25,
};
